import type { AxiosInstance } from 'axios';
import { FormFileUploadClient } from './form-file-upload';
import { AgentResourcesClient } from './agent-resources';
import { AppClient } from './app';
import { AudioStatusClient } from './audio-status';
import { ChatBaseClient } from './chat';
import { ChatChartPreferencesClient } from './chat.charts';
import { ChatContextClient } from './chat-context';
import { ChatInstanceClient } from './chat-instances';
import { ChatReportsClient } from './chat-reports';
import { GroupsClient } from './groups';
import { HcpClient } from './hcp';
import { KolSearchClient } from './kol';
import { LocationClient } from './location';
import { ProjectsClient } from './projects';
import { ProjectsEntitiesClient } from './projects.entities';
import { ProjectQuantAnalysesClient } from './projects.quant-analysis';
import { ProjectsTranscriptsClient } from './projects.transcripts';
import { QueriesClient } from './queries';
import { SearchClient } from './search';
import { SpeechClient } from './speech';
import { TagsClient } from './tags';
import { TextToSpeechClient } from './text-to-speech';
import { TranscriptsClient } from './transcripts';
import { TranscriptsSummaryClient } from './transcripts.summary';
import { UsersClient } from './users';
import { WorkspacesClient } from './workspaces';
import { WorkspaceBrandsClient } from './workspaces.brand';
import WorkspacesFileClient from './workspaces.file';
import { WorkspacesFileUploadClient } from './workspaces.file-upload';
import { WorkspacesFolderClient } from './workspaces.folder';
import { WorkspacesGlobalSearchClient } from './workspaces.global-search';
import { WorkspacesObjectClient } from './workspaces.object';
import { WorkspacesTranscriptClient } from './workspaces.transcript';

type BuildEnhancedChatApiClient = {
  authenticatedClient: AxiosInstance;
  unauthenticatedClient: AxiosInstance;
};

export function buildEnhancedChatApiClient({
  authenticatedClient, unauthenticatedClient,
}: BuildEnhancedChatApiClient) {

  return {
    app: new AppClient(authenticatedClient, unauthenticatedClient),
    audioStatus: new AudioStatusClient(authenticatedClient),
    agentResources: new AgentResourcesClient(authenticatedClient),
    chat: new ChatBaseClient(authenticatedClient),
    chatInstances: new ChatInstanceClient(authenticatedClient),
    chatCharts: new ChatChartPreferencesClient(authenticatedClient),
    chatContext: new ChatContextClient(authenticatedClient),
    chatReports: new ChatReportsClient(authenticatedClient),
    groups: new GroupsClient(authenticatedClient),
    hcp: new HcpClient(authenticatedClient),
    kolSearch: new KolSearchClient(authenticatedClient),
    location: new LocationClient(authenticatedClient),
    projects: new ProjectsClient(authenticatedClient),
    projectsEntities: new ProjectsEntitiesClient(authenticatedClient),
    projectsTranscripts: new ProjectsTranscriptsClient(authenticatedClient),
    quantAnalysis: new ProjectQuantAnalysesClient(authenticatedClient),
    queries: new QueriesClient(authenticatedClient),
    search: new SearchClient(authenticatedClient),
    speech: new SpeechClient(authenticatedClient),
    tags: new TagsClient(authenticatedClient),
    textToSpeech: new TextToSpeechClient(authenticatedClient),
    transcripts: new TranscriptsClient(authenticatedClient),
    workspaces: new WorkspacesClient(authenticatedClient),
    workspaceBrands: new WorkspaceBrandsClient(authenticatedClient),
    workspacesFile: new WorkspacesFileClient(authenticatedClient),
    workspacesFileUpload: new WorkspacesFileUploadClient(authenticatedClient),
    workspacesFolder: new WorkspacesFolderClient(authenticatedClient),
    workspacesObject: new WorkspacesObjectClient(authenticatedClient),
    workspacesGlobalSearch: new WorkspacesGlobalSearchClient(authenticatedClient),
    workspacesTranscript: new WorkspacesTranscriptClient(authenticatedClient),
    transcriptsSummary: new TranscriptsSummaryClient(authenticatedClient),
    users: new UsersClient(authenticatedClient),
    formFileUpload: new FormFileUploadClient(authenticatedClient),

    authenticatedClient,
    unauthenticatedClient,
  };
}

export type ChatAppApiClient = ReturnType<typeof buildEnhancedChatApiClient>;